import axios, { AxiosInstance } from 'axios'
import { HAPTTIC_X_API_KEY } from 'src/constants/env'
import { getSessionIdFromLocalStorage } from 'src/localStorage/sessionId/getSessionIdFromLocalStorage/getSessionIdFromLocalStorage'
import { ANALYTICS_API_URL } from 'src/services/constants/urls'
import { CompanyProfileData } from 'src/pages/Settings/CompanyProfile/types'

// `data` is null until the user has filled the profile in, which is what the
// page reads to decide between the form and the summary.
export interface CompanyProfileResponse {
  data: (CompanyProfileData & { createdAt?: string; updatedAt?: string }) | null
  success: boolean
}

const PATH = '/v1_private/company-profile'

const getHeaders = () => ({
  'Content-Type': 'application/json',
  'X-Api-Key': HAPTTIC_X_API_KEY,
  'x-session-id': getSessionIdFromLocalStorage() || '',
})

class CompanyProfileApiService {
  private static _instance: CompanyProfileApiService

  private client: AxiosInstance

  private constructor() {
    this.client = axios.create({
      baseURL: ANALYTICS_API_URL,
      headers: getHeaders(),
    })
  }

  private static get instance(): CompanyProfileApiService {
    if (!CompanyProfileApiService._instance) {
      CompanyProfileApiService._instance = new CompanyProfileApiService()
    }
    return CompanyProfileApiService._instance
  }

  public static refresh(): void {
    CompanyProfileApiService._instance = new CompanyProfileApiService()
  }

  public static async get(): Promise<CompanyProfileResponse> {
    const response =
      await CompanyProfileApiService.instance.client.get<CompanyProfileResponse>(
        PATH,
        { headers: getHeaders() }
      )
    return response.data
  }

  // The first save. Replaces the whole document.
  public static async replace(
    payload: CompanyProfileData
  ): Promise<CompanyProfileResponse> {
    const response =
      await CompanyProfileApiService.instance.client.put<CompanyProfileResponse>(
        PATH,
        payload,
        { headers: getHeaders() }
      )
    return response.data
  }

  // One section. Only the keys sent are touched.
  public static async update(
    payload: Partial<CompanyProfileData>
  ): Promise<CompanyProfileResponse> {
    const response =
      await CompanyProfileApiService.instance.client.patch<CompanyProfileResponse>(
        PATH,
        payload,
        { headers: getHeaders() }
      )
    return response.data
  }
}

export { CompanyProfileApiService }
